<template>
  <div class="schedule-management">

    <!-- Header Card -->
    <el-card class="schedule-header-card">
      <div class="schedule-header">
        <div class="exam-info">
          <h3>{{ exam?.name || '监考排程' }}</h3>
          <el-tag v-if="exam" type="info">{{ exam.sessions?.length || 0 }} 场次</el-tag>
        </div>
        <div class="actions">
          <el-button @click="goBack">
            <el-icon>
              <Back />
            </el-icon>
            返回
          </el-button>
          <el-button :type="swapMode ? 'danger' : 'info'" @click="toggleSwapMode">
            <el-icon>
              <Switch />
            </el-icon>
            {{ swapMode ? '退出调换模式' : '开启调换模式' }}
          </el-button>
          <el-button type="warning" @click="showUnavailableDialog">
            <el-icon>
              <User />
            </el-icon>
            设置不监考
          </el-button>
          <el-button type="success" @click="exportToExcel" :loading="exporting">
            <el-icon>
              <Download />
            </el-icon>
            导出 Excel
          </el-button>
          <el-button type="primary" @click="autoGenerate" :loading="scheduleStore.generating">
            <el-icon>
              <MagicStick />
            </el-icon>
            自动排程
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- Schedule Grid Tables by Grade -->
    <el-card v-loading="loading" class="schedule-table-card">
      <div v-if="scheduleGrid.length > 0">
        <!-- Loop through grades -->
        <div v-for="grade in gradeList" :key="grade" class="grade-section">
          <h4 class="grade-title">
            <el-tag :type="getGradeTagType(grade)" size="large">{{ grade }}</el-tag>
            监考表
          </h4>

          <div class="schedule-grid">
            <table class="exam-schedule-table">
              <thead>
                <tr>
                  <th rowspan="2" class="fixed-col">试场/时间/科目</th>
                  <!-- 只显示该年级的场次 -->
                  <th v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)" :key="dateIdx"
                    :colspan="dateGroup.sessions.length" class="date-header">
                    {{ formatDate(dateGroup.date) }}
                  </th>
                </tr>
                <tr>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)" :key="'time-'+dateIdx">
                    <th v-for="session in dateGroup.sessions" :key="session.id" class="time-header">
                      <div class="time-slot">{{ session.start_time?.slice(0, 5) }}-{{ session.end_time?.slice(0, 5) }}
                      </div>
                      <div class="subject-name">{{ session.subject_name || session.session_name || '-' }}</div>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="room in getRoomsByGrade(grade)" :key="room.name">
                  <td class="fixed-col room-cell">
                    <div class="room-info">
                      <span class="room-name">{{ room.name }}</span>
                      <span class="room-building">{{ room.building }}</span>
                    </div>
                  </td>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)"
                    :key="'cell-'+dateIdx+'-'+room.name">
                    <td v-for="session in dateGroup.sessions" :key="session.id + '-' + room.name" class="teacher-cell"
                      :class="{ 'swap-active-cell': swapMode }">
                      <div v-for="cell in getScheduleCells(session.id, room)" :key="cell.scheduleId"
                        class="teacher-select-wrapper" :class="{
                          'swap-active': swapMode,
                          'swap-selected': isSelectedForSwap(cell.scheduleId)
                        }" @click="handleCellClick(session.id, room, cell.scheduleId)">
                        <el-select v-model="cell.teacherId" filterable clearable placeholder="选择教师" size="small"
                          @change="(val) => updateTeacher(session.id, room, val, cell.scheduleId)"
                          :class="{ 'manual-override': cell.isManual }" :disabled="swapMode">
                          <el-option v-for="t in availableTeachers(session.id)" :key="t.id" :label="t.name"
                            :value="t.id" :disabled="isTeacherBusy(session.id, room.name, t.id, cell.scheduleId)" />
                        </el-select>
                      </div>
                    </td>
                  </template>
                </tr>
                <!-- 巡考行 -->
                <tr class="patrol-row">
                  <td class="fixed-col room-cell">
                    <div class="room-info">
                      <span class="room-name">巡考</span>
                      <span class="room-building">(0.25h/场)</span>
                    </div>
                  </td>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)"
                    :key="'patrol-'+grade+'-'+dateIdx">
                    <td v-for="session in dateGroup.sessions" :key="'patrol-' + grade + '-' + session.id"
                      class="teacher-cell patrol-cell">
                      <div class="teacher-select-wrapper">
                        <el-select :model-value="getPatrolSchedule(session.id)?.teacher_id" filterable clearable
                          placeholder="选择巡考" size="small" @change="(val) => updatePatrolTeacher(session.id, val)"
                          class="patrol-select">
                          <el-option v-for="t in availableTeachers(session.id)" :key="t.id" :label="t.name"
                            :value="t.id" />
                        </el-select>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <el-empty v-else description="暂无排程数据，请先自动排程" />
    </el-card>

    <!-- Workload Stats Card -->
    <el-card class="workload-card" v-if="workloadStats.length > 0">
      <template #header>
        <span>本次考试监考工作量统计</span>
        <span class="workload-rule">（规则：105min按2h计，巡考不计入）</span>
      </template>
      <el-table :data="workloadStats" size="small" max-height="300">
        <el-table-column prop="teacher_name" label="教师" width="100" />
        <el-table-column label="工作时长" width="100">
          <template #default="{ row }">
            <el-tag :type="getWorkloadType(row.total_hours)" size="small">{{ row.total_hours.toFixed(2) }}h</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="工作量分布">
          <template #default="{ row }">
            <el-progress :percentage="(row.total_hours / maxWorkloadHours) * 100" :stroke-width="12" :show-text="false"
              :color="getWorkloadColor(row.total_hours)" />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Unavailable Dialog -->
    <el-dialog v-model="unavailableDialogVisible" title="设置教师不监考" width="700px">
      <el-tabs v-model="unavailableTab">
        <el-tab-pane label="整场考试不监考" name="exam">
          <el-form inline>
            <el-form-item label="选择教师">
              <el-select v-model="selectedUnavailableTeachers" multiple filterable placeholder="选择不参与本次考试的教师"
                style="width: 400px">
                <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="setExamUnavailable">确定</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="examUnavailableList" size="small" v-if="examUnavailableList.length">
            <el-table-column label="教师">
              <template #default="{ row }">
                {{teachers.find(t => t.id === row.teacher_id)?.name || row.teacher_id}}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="danger" @click="removeUnavailable(row.id)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="特定场次不监考" name="session">
          <el-form inline>
            <el-form-item label="选择教师">
              <el-select v-model="sessionUnavailableForm.teacher_id" filterable placeholder="选择教师">
                <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="选择场次">
              <el-select v-model="sessionUnavailableForm.session_ids" multiple placeholder="选择不可监考的场次">
                <el-option v-for="s in exam?.sessions" :key="s.id"
                  :label="`${s.exam_date} ${s.start_time?.slice(0, 5)} ${s.session_name || ''}`" :value="s.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="setSessionUnavailable">确定</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="sessionUnavailableList" size="small" v-if="sessionUnavailableList.length">
            <el-table-column label="教师">
              <template #default="{ row }">
                {{teachers.find(t => t.id === row.teacher_id)?.name || row.teacher_id}}
              </template>
            </el-table-column>
            <el-table-column label="不可监考场次">
              <template #default="{ row }">
                {{ getSessionDisplay(row.exam_session_id) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="danger" @click="removeUnavailable(row.id)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Back, MagicStick, User, Switch, Download } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { examApi, scheduleApi, teacherApi } from '@/api'
import { useScheduleStore } from '@/stores/schedule'

const scheduleStore = useScheduleStore()

const router = useRouter()
const route = useRoute()
const examId = computed(() => Number(route.params.examId))

const exam = ref(null)
const teachers = ref([])
const schedules = ref([])
const unavailableList = ref([])
const loading = ref(false)
const exporting = ref(false)

// Swap Mode
const swapMode = ref(false)
const selectedSwapCell = ref(null) // { sessionId, roomName, scheduleId }

// Unavailable dialog
const unavailableDialogVisible = ref(false)
const unavailableTab = ref('exam')
const selectedUnavailableTeachers = ref([])
const sessionUnavailableForm = ref({ teacher_id: null, session_ids: [] })

// Schedule cell map for quick access
const scheduleCellMap = ref({})

// Note: roomList was moved to getRoomsByGrade function

// Computed: Get unique grades from exam sessions (not schedules)
// 新模型：年级信息在场次上
const gradeList = computed(() => {
  if (!exam.value?.sessions) return []

  const grades = new Set()
  exam.value.sessions.forEach(s => {
    grades.add(s.grade || '未分年级')
  })

  if (grades.size === 0) return []

  // Sort: 高一, 高二, 高三, 未分年级
  const order = ['高一', '高二', '高三', '未分年级']
  return Array.from(grades).sort((a, b) => {
    const ai = order.indexOf(a)
    const bi = order.indexOf(b)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
})

// Get rooms for a specific grade (from sessions that have that grade)
// 新模型：从该年级的场次中收集考场
const getRoomsByGrade = (grade) => {
  if (!exam.value?.sessions) return []

  const normalizedGrade = grade === '未分年级' ? null : grade
  const gradeSessionIds = new Set(
    exam.value.sessions
      .filter(s => (s.grade || null) === normalizedGrade)
      .map(s => s.id)
  )

  // 从排程中找出属于这些场次的考场（去重）
  const rooms = new Map()
  schedules.value.forEach(s => {
    if (gradeSessionIds.has(s.exam_session_id) && s.room_name && s.role !== '巡考') {
      const key = `${s.building || ''}_${s.room_name}`
      if (!rooms.has(key)) {
        rooms.set(key, {
          id: s.exam_room_id,
          name: s.room_name,
          building: s.building || ''
        })
      }
    }
  })

  return Array.from(rooms.values()).sort((a, b) => {
    const strA = a.building || a.name
    const strB = b.building || b.name
    return strA.localeCompare(strB, 'zh', { numeric: true })
  })
}

// Get sessions for a specific grade, grouped by date
// 新模型：只返回该年级的场次
const getSessionsByGrade = (grade) => {
  if (!exam.value?.sessions) return []

  const normalizedGrade = grade === '未分年级' ? null : grade
  const gradeSessions = exam.value.sessions
    .filter(s => (s.grade || null) === normalizedGrade)
    .sort((a, b) => {
      // 先按日期排序，再按时间排序
      const dateCompare = a.exam_date.localeCompare(b.exam_date)
      if (dateCompare !== 0) return dateCompare
      return a.start_time.localeCompare(b.start_time)
    })

  // 按日期分组
  const groups = {}
  gradeSessions.forEach(s => {
    if (!groups[s.exam_date]) {
      groups[s.exam_date] = { date: s.exam_date, sessions: [] }
    }
    groups[s.exam_date].sessions.push(s)
  })

  return Object.values(groups).sort((a, b) => a.date.localeCompare(b.date))
}

// Grade tag type
const getGradeTagType = (grade) => {
  const types = { '高一': 'success', '高二': 'warning', '高三': 'danger' }
  return types[grade] || 'info'
}

// Note: Unified time slots removed - each grade shows only its own sessions

// Build schedule grid for display
const scheduleGrid = computed(() => schedules.value)

// Workload stats from backend
const workloadStats = ref([])

const maxWorkloadHours = computed(() => {
  return Math.max(...workloadStats.value.map(w => w.total_hours), 1)
})

// Filter unavailable lists
const examUnavailableList = computed(() =>
  unavailableList.value.filter(u => !u.exam_session_id)
)

const sessionUnavailableList = computed(() =>
  unavailableList.value.filter(u => u.exam_session_id)
)

// Build cell map for quick access
// 使用 session_id + room_name 作为 key，因为同一物理考场在不同场次有不同的 exam_room_id
watch(schedules, (newSchedules) => {
  const map = {}
  newSchedules.forEach(s => {
    if (s.role !== '巡考') {
      const key = `${s.exam_session_id}-${s.room_name}`
      if (!map[key]) {
        map[key] = []
      }
      map[key].push({
        scheduleId: s.id,
        teacherId: s.teacher_id,
        isManual: s.is_manual_override,
        examRoomId: s.exam_room_id
      })
    }
  })
  // Sort them so they display consistently
  Object.values(map).forEach(slots => {
    slots.sort((a, b) => a.scheduleId - b.scheduleId)
  })
  scheduleCellMap.value = map
}, { immediate: true })

// Get cell data - 使用 room_name 匹配
const getScheduleCells = (sessionId, room) => {
  const key = `${sessionId}-${room.name}`
  return scheduleCellMap.value[key] || []
}

// Check if teacher is already assigned in this session
const isTeacherBusy = (sessionId, currentRoomName, teacherId, scheduleId) => {
  return schedules.value.some(s =>
    s.exam_session_id === sessionId &&
    s.teacher_id === teacherId &&
    s.id !== scheduleId &&
    s.role !== '巡考'
  )
}

// Get available teachers for a session (excluding unavailable ones)
const availableTeachers = (sessionId) => {
  const unavailableTeacherIds = new Set(
    unavailableList.value
      .filter(u => !u.exam_session_id || u.exam_session_id === sessionId)
      .map(u => u.teacher_id)
  )
  return teachers.value.filter(t => !unavailableTeacherIds.has(t.id))
}

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return `${d.getMonth() + 1}.${d.getDate()}（${weekdays[d.getDay()]}）`
}

// Note: getSessionSubject removed - using session.subject_name directly in template

// Get session display text
const getSessionDisplay = (sessionId) => {
  const session = exam.value?.sessions?.find(s => s.id === sessionId)
  if (!session) return sessionId
  return `${session.exam_date} ${session.start_time?.slice(0, 5)} ${session.session_name || ''}`
}

// Note: getPatrolTeacher removed - using getPatrolSchedule instead

// Get patrol schedule object for editing
const getPatrolSchedule = (sessionId) => {
  return schedules.value.find(s =>
    s.exam_session_id === sessionId && s.role === '巡考'
  ) || null
}

// Update patrol teacher
const updatePatrolTeacher = async (sessionId, teacherId) => {
  const patrol = getPatrolSchedule(sessionId)
  if (!patrol) {
    ElMessage.warning('该场次没有巡考记录')
    return
  }

  try {
    await scheduleApi.update(patrol.id, { teacher_id: teacherId })
    ElMessage.success('巡考老师已更新')

    // Update local data
    patrol.teacher_id = teacherId
    patrol.teacher_name = teachers.value.find(t => t.id === teacherId)?.name || ''
    patrol.is_manual_override = true

    // Refresh workload stats
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  } catch (e) {
    console.error(e)
    fetchSchedules()
  }
}

// Workload color based on hours
const getWorkloadColor = (hours) => {
  const avg = workloadStats.value.length > 0
    ? workloadStats.value.reduce((sum, w) => sum + w.total_hours, 0) / workloadStats.value.length
    : 0
  if (hours > avg * 1.3) return '#f56c6c'
  if (hours < avg * 0.7) return '#e6a23c'
  return '#67c23a'
}

const getWorkloadType = (hours) => {
  const avg = workloadStats.value.length > 0
    ? workloadStats.value.reduce((sum, w) => sum + w.total_hours, 0) / workloadStats.value.length
    : 0
  if (hours > avg * 1.3) return 'danger'
  if (hours < avg * 0.7) return 'warning'
  return 'success'
}

// Fetch data
const fetchExam = async () => {
  try {
    exam.value = await examApi.getById(examId.value)
  } catch (e) {
    console.error(e)
  }
}

const fetchTeachers = async () => {
  try {
    teachers.value = await teacherApi.getAll()
  } catch (e) {
    console.error(e)
  }
}

const fetchSchedules = async () => {
  loading.value = true
  try {
    schedules.value = await scheduleApi.getAll(examId.value)
    // Also fetch workload stats for this exam
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const fetchUnavailable = async () => {
  try {
    let allUnavailable = []
    for (const teacher of teachers.value) {
      const unavailable = await teacherApi.getUnavailable(teacher.id, examId.value)
      allUnavailable = allUnavailable.concat(unavailable)
    }
    unavailableList.value = allUnavailable
  } catch (e) {
    console.error(e)
  }
}

// Update teacher assignment
const updateTeacher = async (sessionId, room, teacherId, scheduleId) => {
  if (!scheduleId) {
    ElMessage.warning('该单元格没有排程记录')
    return
  }

  try {
    await scheduleApi.update(scheduleId, { teacher_id: teacherId })
    ElMessage.success('已保存')

    // Update local data
    const schedule = schedules.value.find(s => s.id === scheduleId)
    if (schedule) {
      schedule.teacher_id = teacherId
      schedule.teacher_name = teachers.value.find(t => t.id === teacherId)?.name || ''
      schedule.is_manual_override = true
    }

    // Rebuild cell map (we update the specific array item)
    const key = `${sessionId}-${room.name}`
    if (scheduleCellMap.value[key]) {
      const slot = scheduleCellMap.value[key].find(c => c.scheduleId === scheduleId)
      if (slot) {
        slot.teacherId = teacherId
        slot.isManual = true
      }
    }

    // Refresh workload stats
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  } catch (e) {
    console.error(e)
    fetchSchedules()
  }
}

// Toggle Swap Mode
const toggleSwapMode = () => {
  swapMode.value = !swapMode.value
  selectedSwapCell.value = null
  if (swapMode.value) {
    ElMessage.info('调换模式已开启，请依次点击两个监考单元格进行互换')
  }
}

// Handle Cell Click (for Swap Mode)
const handleCellClick = async (sessionId, room, scheduleId) => {
  if (!swapMode.value) return
  if (!scheduleId) return

  if (!selectedSwapCell.value) {
    // Select first cell
    selectedSwapCell.value = {
      sessionId,
      roomName: room.name,
      scheduleId: scheduleId
    }
  } else {
    // Select second cell and swap
    const first = selectedSwapCell.value
    const second = {
      sessionId,
      roomName: room.name,
      scheduleId: scheduleId
    }

    if (first.scheduleId === second.scheduleId) {
      selectedSwapCell.value = null
      return
    }

    try {
      loading.value = true
      await scheduleApi.swap(first.scheduleId, second.scheduleId)
      ElMessage.success('调换成功')
      await fetchSchedules()
    } catch (e) {
      console.error(e)
      // Error message handled by interceptor
    } finally {
      loading.value = false
      selectedSwapCell.value = null
    }
  }
}

const isSelectedForSwap = (scheduleId) => {
  return selectedSwapCell.value?.scheduleId === scheduleId
}

// Export to Excel
const exportToExcel = async () => {
  exporting.value = true
  try {
    const response = await examApi.exportExcel(examId.value)

    // Create a link and trigger download
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = url

    // Try to get filename from content-disposition if possible, or use default
    const filename = `监考表_${exam.value?.name || examId.value}_${new Date().toLocaleDateString()}.xlsx`

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    ElMessage.success('导出成功')
  } catch (e) {
    console.error('Export failed:', e)
    ElMessage.error('导出失败，请重试')
  } finally {
    exporting.value = false
  }
}

// Auto generate
const autoGenerate = async () => {
  try {
    await ElMessageBox.confirm(
      '自动排程将清除现有排程并重新生成，是否继续？',
      '确认自动排程',
      { type: 'warning' }
    )

    scheduleStore.startGenerating(examId.value)

    // 模拟进度更新
    const progressInterval = setInterval(() => {
      if (scheduleStore.progressPercent < 30) {
        scheduleStore.updateProgress('正在加载教师和考场信息...', scheduleStore.progressPercent + 5)
      } else if (scheduleStore.progressPercent < 50) {
        scheduleStore.updateProgress('正在分配巡考老师...', scheduleStore.progressPercent + 3)
      } else if (scheduleStore.progressPercent < 80) {
        scheduleStore.updateProgress('正在优化监考老师分配...', scheduleStore.progressPercent + 2)
      } else if (scheduleStore.progressPercent < 95) {
        scheduleStore.updateProgress('正在平衡工作量...', scheduleStore.progressPercent + 1)
      }
    }, 300)

    const result = await scheduleApi.autoGenerate(examId.value)

    clearInterval(progressInterval)
    scheduleStore.updateProgress('排程完成！', 100)

    await new Promise(resolve => setTimeout(resolve, 500))

    if (result.success) {
      ElMessage.success(result.message || '排程成功')
    } else {
      ElMessage.warning({
        message: result.message || '排程未完全成功，请检查约束条件',
        duration: 5000,
        showClose: true
      })
    }
    fetchSchedules()
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      ElMessage.error('排程启动失败')
    }
  } finally {
    scheduleStore.finishGenerating()
  }
}

// Watch for global scheduling completion to refresh data if we are on the same exam page
watch(() => scheduleStore.generating, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    if (scheduleStore.currentExamId === examId.value) {
      fetchSchedules()
    }
  }
})

const goBack = () => {
  router.push('/exams')
}

// Unavailable management
const showUnavailableDialog = () => {
  unavailableDialogVisible.value = true
  fetchUnavailable()
}

const setExamUnavailable = async () => {
  if (!selectedUnavailableTeachers.value.length) {
    ElMessage.warning('请选择教师')
    return
  }
  try {
    for (const teacherId of selectedUnavailableTeachers.value) {
      await teacherApi.setUnavailable({
        teacher_id: teacherId,
        exam_id: examId.value,
        exam_session_id: null
      })
    }
    ElMessage.success('已设置')
    selectedUnavailableTeachers.value = []
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

const setSessionUnavailable = async () => {
  const { teacher_id, session_ids } = sessionUnavailableForm.value
  if (!teacher_id || !session_ids.length) {
    ElMessage.warning('请选择教师和场次')
    return
  }
  try {
    for (const sessionId of session_ids) {
      await teacherApi.setUnavailable({
        teacher_id,
        exam_id: examId.value,
        exam_session_id: sessionId
      })
    }
    ElMessage.success('已设置')
    sessionUnavailableForm.value = { teacher_id: null, session_ids: [] }
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

const removeUnavailable = async (id) => {
  try {
    await teacherApi.removeUnavailable(id)
    ElMessage.success('已移除')
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

onMounted(async () => {
  await Promise.all([fetchExam(), fetchTeachers()])
  await fetchSchedules()
  fetchUnavailable()
})
</script>

<style scoped>
.schedule-management {
  max-width: 1800px;
  position: relative;
}

.scheduling-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scheduling-progress {
  background: white;
  padding: 40px 60px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.scheduling-progress h3 {
  margin: 15px 0 10px;
  color: #303133;
}

.scheduling-progress .progress-step {
  color: #409eff;
  font-weight: 500;
  margin: 10px 0;
}

.scheduling-progress .progress-hint {
  color: #909399;
  font-size: 12px;
  margin-top: 15px;
}

.schedule-header-card {
  margin-bottom: 20px;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.exam-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.exam-info h3 {
  margin: 0;
  color: #1f2937;
}

.actions {
  display: flex;
  gap: 10px;
}

.schedule-table-card {
  margin-bottom: 20px;
  overflow-x: auto;
}

.schedule-grid {
  overflow-x: auto;
}

.exam-schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.exam-schedule-table th,
.exam-schedule-table td {
  border: 1px solid #e5e7eb;
  padding: 8px;
  text-align: center;
}

.exam-schedule-table th {
  background: #f3f4f6;
  color: #374151;
  font-weight: 500;
}

.fixed-col {
  position: sticky;
  left: 0;
  background: #ffffff !important;
  z-index: 10;
  min-width: 120px;
}

.date-header {
  background: #e0e7ff !important;
  color: #4338ca !important;
  font-size: 14px;
}

.time-header {
  min-width: 100px;
}

.time-slot {
  font-size: 12px;
  color: #6b7280;
}

.subject-name {
  color: #1f2937;
  font-weight: 600;
}

.room-cell {
  text-align: left !important;
}

.room-info {
  display: flex;
  flex-direction: column;
}

.room-name {
  font-weight: 600;
  color: #1f2937;
}

.room-building {
  font-size: 11px;
  color: #6b7280;
}

.teacher-cell {
  padding: 4px !important;
  min-width: 100px;
  transition: all 0.3s;
}

.teacher-select-wrapper {
  margin-bottom: 4px;
  border-radius: 4px;
}

.teacher-select-wrapper:last-child {
  margin-bottom: 0;
}

.teacher-select-wrapper.swap-active {
  cursor: pointer;
  padding: 2px;
}

.teacher-select-wrapper.swap-active:hover {
  background-color: rgba(64, 158, 255, 0.1);
}

.teacher-select-wrapper.swap-selected {
  background-color: rgba(64, 158, 255, 0.2) !important;
  outline: 2px solid #409eff;
  outline-offset: -2px;
}



.teacher-cell.no-session-cell {
  background: #f3f4f6;
}

.empty-cell {
  text-align: center;
  color: #d1d5db;
  font-size: 12px;
}

.time-header.no-session {
  background: #f3f4f6;
  color: #9ca3af;
}

.teacher-select-wrapper :deep(.el-select) {
  width: 100%;
}

.teacher-select-wrapper :deep(.el-select__wrapper) {
  background: #f9fafb;
  min-height: 28px;
}

.teacher-select-wrapper :deep(.manual-override .el-select__wrapper) {
  background: rgba(251, 191, 36, 0.15);
  border-color: rgba(251, 191, 36, 0.5);
}

.patrol-row {
  background: #f9fafb;
}

.patrol-cell {
  color: #6b7280;
}

.workload-card {
  max-width: 500px;
}

.workload-rule {
  font-size: 12px;
  color: #a0aec0;
  margin-left: 8px;
  font-weight: normal;
}

.workload-card :deep(.el-card__header) {
  padding: 12px 16px;
}

.grade-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.grade-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.grade-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 16px;
}
</style>
